import React, { useEffect, useMemo, useRef, useState } from 'react';

interface EmailHtmlFrameProps {
  html: string;
  title?: string;
}

/**
 * Render email HTML inside a sandboxed iframe so <style> blocks apply
 * without leaking into the app chrome.
 */
export default function EmailHtmlFrame({ html, title = 'Email' }: EmailHtmlFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(480);

  const srcDoc = useMemo(() => {
    // Force a readable light canvas for marketing emails; most are authored for light bg.
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<base target="_blank" rel="noopener noreferrer"/>
<style>
  html, body { margin: 0; padding: 0; background: #ffffff; color: #121820; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
  img { max-width: 100% !important; height: auto !important; }
  table { max-width: 100% !important; }
</style>
</head>
<body>${html}</body>
</html>`;
  }, [html]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const resize = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const h = Math.max(
          doc.body?.scrollHeight || 0,
          doc.documentElement?.scrollHeight || 0,
          240
        );
        setHeight(Math.min(h + 16, 4000));
      } catch {
        // cross-origin shouldn't happen with srcDoc
      }
    };

    iframe.addEventListener('load', resize);
    // Retry a few times as images load
    const t1 = window.setTimeout(resize, 300);
    const t2 = window.setTimeout(resize, 1000);
    const t3 = window.setTimeout(resize, 2500);
    return () => {
      iframe.removeEventListener('load', resize);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [srcDoc]);

  return (
    <iframe
      ref={iframeRef}
      title={title}
      srcDoc={srcDoc}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      referrerPolicy="no-referrer"
      style={{
        width: '100%',
        height,
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 8,
        background: '#fff',
      }}
    />
  );
}
